import React, { useMemo, useCallback } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import Notification, { NotificationColor } from './Notification'
import { removeNotification } from 'store/notifications'
import { RootState } from 'types'

export interface NotificationItem {
  id: number
  color: NotificationColor
  text: string
}

const NotificationList: React.FC = () => {
  const dispatch = useDispatch()
  const notifications = useSelector<RootState, NotificationItem[]>(
    state => (state.notifications as NotificationItem[]) || [],
  )

  // Create a stable handler that uses the notification ID
  // This prevents creating new functions on every render
  const handleDismiss = useCallback(
    (notification: NotificationItem) => {
      dispatch(removeNotification(notification))
    },
    [dispatch],
  )

  // Create a stable map of notification IDs to dismiss handlers
  const dismissHandlers = useMemo(() => {
    const handlers = new Map<number, () => void>()
    notifications.forEach(n => {
      // Create a bound handler for each notification
      handlers.set(n.id, () => handleDismiss(n))
    })
    return handlers
  }, [notifications, handleDismiss])

  return (
    <div className="notification-list">
      <div className="container">
        {notifications.map(n => {
          return (
            <Notification
              autoDismissTimeout={15000}
              dismissible
              key={n.id}
              shadow
              {...n}
              onDismiss={dismissHandlers.get(n.id)}
            />
          )
        })}
      </div>
      <style>
        {`
          .notification-list {
            position: sticky;
            top: 2rem;
            z-index: 1050;
          }
          .container {
            position: absolute;
          }
        `}
      </style>
    </div>
  )
}

export default NotificationList
